import {
	Gift,
	Handshake,
	Info,
	LifeBuoy,
	Map as MapIcon,
	Zap,
	Sparkles,
	Star,
	Grid2X2,
	Heart,
	Home,
	Music2,
	UsersRound,
	AudioWaveform,
} from "lucide-react";

import { NavMain, type NavMainItems } from "./nav-main";
import { NavSecondary, type NavSecondaryItems } from "./nav-secondary";
import { NavUser } from "./nav-user";
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
} from "@/components/ui/sidebar";
import { Link } from "react-router";
import ThemeSelector from "./theme-selector";
import { useAudio } from "@/app/providers/audio-provider";
import { useQuery } from "@tanstack/react-query";
import { useApp } from "@/app/providers/app-provider";
import { useHasUnseenRewards } from "@/hooks/rewards-hooks";
import { buzzHomeUrl } from "@/features/the-buzz/the-buzz-api";

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
	const { appStore, isSubscribed } = useApp();
	const isLoggedIn = !!appStore.session?.user?.id;
	const { audioRepository } = useAudio();
	const { data: unseenData } = useHasUnseenRewards();
	const { data: publicAudioGroups } = useQuery({
		queryKey: ["publicAudioGroups"],
		queryFn: () => audioRepository.getPublicGroups(),
	});

	const data: {
		navMain: NavMainItems;
		navSecondary: NavSecondaryItems;
	} = {
		navMain: [
			{
				title: "Explore",
				url: "/library",
				icon: MapIcon,
			},
			{
				title: "By Collection",
				url: "#", // this will open the subnav rather than link to a new page
				icon: Grid2X2,
				items: [],
			},
			{
				title: "By Effect",
				url: "#", // this will open the subnav rather than link to a new page
				icon: Sparkles,
				items: [],
			},
		],
		navSecondary: [
			{
				title: "About Us",
				url: "/about",
				icon: Info,
			},
			{
				title: "Made For",
				icon: UsersRound,
				items: [
					{
						title: "Writers",
						url: "/made-for/writers",
					},
					{
						title: "Remote Workers",
						url: "/made-for/remote-workers",
					},
					{
						title: "Students",
						url: "/made-for/students",
					},
					{
						title: "Developers",
						url: "/made-for/developers",
					},
					{
						title: "Teachers",
						url: "/made-for/teachers",
					},
				],
			},
			{
				title: "Support",
				url: `${buzzHomeUrl}/help/`,
				external: true,
				icon: LifeBuoy,
			},
			{
				title: "Pricing",
				url: "/pricing",
				icon: Star,
			},
			{
				title: "The Buzz",
				url: buzzHomeUrl,
				external: true,
				icon: Zap,
			},
			{
				title: "Terms",
				url: "/terms",
				icon: Handshake,
			},
		],
	};

	const freeGroup = publicAudioGroups?.free;
	const audioCollections = publicAudioGroups?.collections ?? [];
	const audioEffects = publicAudioGroups?.effects ?? [];

	// if there are audio collections, add them to the navMain
	if (audioCollections.length > 0) {
		const collectionData = data.navMain.find(
			(item) => item.title === "By Collection",
		);
		if (collectionData) {
			collectionData.items = audioCollections.map((collection) => ({
				title: collection.name,
				url: `/library/collections/${collection.slug}`,
			}));
		}
	}

	// if there are audio effects, add them to the navMain
	if (audioEffects.length > 0) {
		const effectData = data.navMain.find((item) => item.title === "By Effect");
		if (effectData) {
			effectData.items = audioEffects.map((effect) => ({
				title: effect.name,
				url: `/library/effects/${effect.slug}`,
			}));
		}
	}

	if (isLoggedIn) {
		data.navMain.unshift({
			title: "Home",
			url: "/home",
			icon: Home,
		});

		data.navMain.splice(1, 0, {
			title: "Favorites",
			url: "/library/favorites",
			icon: Heart,
		});

		data.navMain.splice(2, 0, {
			title: "Custom Blends",
			url: "/library/custom-blends",
			icon: AudioWaveform,
		});
		// Add Rewards link for free-tier users
		if (!isSubscribed) {
			const hasUnseen = unseenData?.hasUnseenRewards;
			const pricingIndex = data.navSecondary.findIndex(
				(item) => item.title === "Pricing",
			);
			const insertAt =
				pricingIndex >= 0 ? pricingIndex + 1 : data.navSecondary.length;
			data.navSecondary.splice(insertAt, 0, {
				title: "Rewards",
				url: "/rewards",
				icon: Gift,
				badge: hasUnseen ? (
					<span className="h-2 w-2 rounded-full bg-primary" />
				) : undefined,
			});
		}
	} else {
		// lets pull out the free collection and put it at the top level
		if (freeGroup) {
			data.navMain.unshift({
				title: "Free Sounds",
				url: `/library/free`,
				icon: Music2,
			});
		}
	}

	return (
		<Sidebar variant="inset" {...props}>
			<SidebarHeader>
				<SidebarMenu>
					<SidebarMenuItem>
						<SidebarMenuButton size="lg" asChild>
							<Link to="/">
								<div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
									<img className="" src="/logos/icon.png" alt="Logo" />
								</div>
								<div className="grid flex-1 text-left text-lg leading-tight">
									<span className="font-[Lora] truncate font-semibold">
										coffitivity
									</span>
								</div>
							</Link>
						</SidebarMenuButton>
					</SidebarMenuItem>
				</SidebarMenu>
			</SidebarHeader>
			<SidebarContent>
				<NavMain items={data.navMain} />
				<NavSecondary items={data.navSecondary} className="mt-auto" />
			</SidebarContent>
			<SidebarFooter>
				<NavUser />
				<ThemeSelector />
			</SidebarFooter>
		</Sidebar>
	);
}
